import { getConfigurationHubObjectParsed } from "~/helpers";
import { MongoDbConnection } from "~/services/MongoDbConnection";
import type {
  DataReplicaConfigSource,
  IPublicPagesReplicas
} from "~/types/app";

type CollectionName =
  | "articles"
  | "journals"
  | "researchTopics"
  | "researchTopicsEbooks"
  | "researchTopicsSponsorships";

let legacyMongoConn!: MongoDbConnection;
let replicator2025MongoConn!: MongoDbConnection;
let isInitialized = false;

export const useMongoDbConnectionManager = () => {
  const {
    mongodbUri,
    mongodbIsDirectConn,
    replicator2025MongodbUri,
    replicator2025MongodbIsDirectConn,
    publicPagesReplicaConfig
  } = useRuntimeConfig();
  const publicPagesReplicaConfigParsed =
    getConfigurationHubObjectParsed<IPublicPagesReplicas>(
      publicPagesReplicaConfig as string
    );

  function getDbNameFromUri(uri: string): string {
    const regex = /(?<=mongodb(?:\+srv)?:\/\/[^/]+\/)[^?]+/;
    const match = uri.match(regex);

    if (match) {
      return match[0];
    } else {
      throw Error("Mongo db name cannot be extracted from the URI");
    }
  }

  function initConnections(): void {
    if (isInitialized) return;

    if (!legacyMongoConn && mongodbUri) {
      legacyMongoConn = new MongoDbConnection(
        mongodbUri as string,
        mongodbIsDirectConn?.toString() === "true"
      );
    }

    if (!replicator2025MongoConn && replicator2025MongodbUri) {
      replicator2025MongoConn = new MongoDbConnection(
        replicator2025MongodbUri as string,
        replicator2025MongodbIsDirectConn?.toString() === "true"
      );
    }

    isInitialized = true;
  }

  function getMongoDbCollectionName(collectionName: CollectionName): string {
    if (collectionName === "articles") {
      return "articles";
    } else if (collectionName === "journals") {
      return "journals";
    } else if (collectionName === "researchTopics") {
      return "research-topics";
    } else if (collectionName === "researchTopicsEbooks") {
      return "research-topic-ebooks";
    } else if (collectionName === "researchTopicsSponsorships") {
      return "research-topics-sponsorship";
    } else {
      throw Error(`Invalid collection name: ${collectionName}`);
    }
  }

  function getConnectionForCollection(collectionName: CollectionName): {
    connection: MongoDbConnection;
    dbName: string;
    dataSource: DataReplicaConfigSource;
  } {
    if (!publicPagesReplicaConfigParsed) {
      throw Error("Public pages replica config not initialized");
    }

    const dataSource = publicPagesReplicaConfigParsed.mongoDB[collectionName];

    if (dataSource === "replicator2025") {
      return {
        connection: replicator2025MongoConn,
        dbName: getDbNameFromUri(replicator2025MongodbUri as string),
        dataSource
      };
    } else {
      return {
        connection: legacyMongoConn,
        dbName: getDbNameFromUri(mongodbUri as string),
        dataSource
      };
    }
  }

  const getCollectionWithMetadata = async (collectionName: CollectionName) => {
    if (!publicPagesReplicaConfigParsed) {
      throw Error("Public pages replica config not initialized");
    }

    const { connection, dbName, dataSource } =
      getConnectionForCollection(collectionName);

    if (!connection.client) {
      await connection.connect();
    }

    return {
      collection: connection.client
        .db(dbName)
        .collection(getMongoDbCollectionName(collectionName)),
      dataSource
    };
  };

  // Initialize connections on first use
  initConnections();

  return {
    getCollectionWithMetadata
  };
};
